import { useContext, useState } from "react";
import Loader from "../../Loader";
import linkedin from "../../../Utils/linkedin.png";
import { routeNames } from "../../../routeSegments";
import { TableHeaderContext } from "../../../Contexts/TableHeaderContext";
import { Tooltip } from "antd";
import { BsTelephoneFill } from "react-icons/bs";
import { MdEmail } from "react-icons/md";
import { PhoneOutgoing, X } from "lucide-react";
import { useManagementPhoneAccess } from "../../../hooks/useManagementPhoneAccess";
import { useParams } from "react-router";

/**
 * The code defines a React functional component called InvestorProfileHighlights that renders a
 * profile highlights section with dynamic data and loading state.
 * @returns The code is returning a JSX element, which represents the structure and content of the
 * InvestorProfileHighlights component.
 */
const InvestorProfileHighlights = ({ data, profile, isLoading }) => {
  const [showContactPopup, setShowContactPopup] = useState(false);
  const { investorProfileKeys } = useContext(TableHeaderContext);
  const { investorId } = useParams();
  
  const {
    showConfirmPopup,
    showPaymentPopup,
    phoneNumbers,
    loadingPhone,
    showEmailOptions, 
    emailSelector,
    onGetPhoneNumber,
    confirmPopup,
    paymentPopup
  } = useManagementPhoneAccess(investorId, "PE-VC");
  

  const renderBoardInfo = (boardInfo) => {
    if (!boardInfo || boardInfo.length === 0) return null;

    const concatenatedInfo = boardInfo
      .map((info) => `${info.name} (${info.designation})`)
      .join(", ");

    if (concatenatedInfo?.length > 29) {
      return (
        <Tooltip
          color="white"
          title={
            <div style={{ color: "black" }}>
              {boardInfo.map((info, index) => {
                const name = info.name;
                const additionalInfo = [
                  info.designation,
                  info.belongs_to_firm_name,
                ]
                  .filter(Boolean)
                  .join(", ");
                const searchQuery = additionalInfo
                  ? `${name} ${additionalInfo} site:linkedin.com`
                  : `${name} site:linkedin.com`;
                return (
                  <p key={index} className="my-1">
                    <a
                      href={`https://www.google.com/search?q=${encodeURIComponent(
                        searchQuery
                      )}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[#a07924] hover:text-[#a07924]"
                    >
                      {name}
                    </a>
                    {additionalInfo && `, ${additionalInfo}.`}
                  </p>
                );
              })}
            </div>
          }
          placement="topLeft"
        >
          <a className="text-[#a07924]">{boardInfo[0].name},</a>
          <span>
            {" "}
            {boardInfo[0].designation}
            {concatenatedInfo.length > 23 && "..."}
          </span>
        </Tooltip>
      );
    } else {
      return boardInfo.map((info, index) => {
        const name = info.name;
        const additionalInfo = [info.designation, info.belongs_to_firm_name]
          .filter(Boolean)
          .join(", ");
        const searchQuery = additionalInfo
          ? `${name} ${additionalInfo} site:linkedin.com`
          : `${name} site:linkedin.com`;
        return (
          <p key={index} className="my-1">
            <a
              href={`https://www.google.com/search?q=${encodeURIComponent(
                searchQuery
              )}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#a07924]"
            >
              {name}
            </a>
            {additionalInfo ? ", " + additionalInfo : ""}
          </p>
        );
      });
    }
  };
  
  const isAngelOnly = !(data?.investments_through?.length > 0) && data?.angel_investments?.length > 0;

  const getAlreadyInvestedAmount = () =>
    data?.investments_through?.reduce((total, deal) => deal !== null ? total + parseFloat(deal.amount_m || 0) : 0, 0).toFixed(2) || null;
  
  
  const getAlreadyExitedAmount = () => 
    data?.exits_through?.reduce((total, deal) => deal !== null ? total + parseFloat(deal.amount_m || 0) : 0, 0).toFixed(2) || null;

  const renderSectorMatch = (sectorMatch, sectorReason, sectorSourceUrl) => {
    if (sectorMatch === null || sectorMatch === '' || sectorMatch === undefined) {
      return '';
    }
    
    if (sectorMatch === 1 || sectorMatch === '1') {
      return (
        <Tooltip
          title={
            <>
              <span className="text-black">{sectorReason}</span>
              {sectorReason !== "" && sectorReason!==null && sectorSourceUrl !== ""  && sectorSourceUrl!==null &&(
                <span>
                  &nbsp;
                   <span className="text-black">(</span>
                   <a
                    style={{ color: "#a07924" }}
                    href={sectorSourceUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Source
                  </a>
                   <span className="text-black">)</span>
                </span>
              )}
            </>
          }
          color="white"
          placement="top"
          zIndex={10000}
        >
          <span
            style={{
              background: "#d4edda",
              color: "#155724",
              padding: "2px 6px",
              borderRadius: "4px",
              fontSize: "11px",
              marginLeft: "4px",
              cursor: "pointer",
              display: "inline-block",
            }}
          >
            Exact
          </span>
        </Tooltip>
      );
    }
    
    if (sectorMatch === 0 || sectorMatch === '0') {
      return (
        <Tooltip
          title={
            <>
              <span className="text-black">{sectorReason}</span>
              {sectorReason !== "" && sectorReason!==null && sectorSourceUrl !== ""  && sectorSourceUrl!==null && (
                <span>
                  &nbsp;
                   <span className="text-black">(</span>
                   <a
                    style={{ color: "#a07924" }}
                    href={sectorSourceUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Source
                  </a>
                   <span className="text-black">)</span>
                </span>
              )}
            </>
          }
          color="white"
          placement="top"
          zIndex={10000}
        >
          <span
            style={{
              background: "#fff3cd",
              color: "#856404",
              padding: "2px 6px",
              borderRadius: "4px",
              fontSize: "11px",
              marginLeft: "4px",
              cursor: "pointer",
              display: "inline-block",
            }}
          >
            Approx
          </span>
        </Tooltip>
      );
    }
    
    return '';
  };

  const getLimitedPartners = (LP) => {
    if (!LP || LP.length === 0) return null;

    return (
      <span>
        {LP.map((partner, index) =>
          partner.id && partner.id !== "" ? (
            <span key={partner.id}>
              <a
                target="_blank"
                href={routeNames.investorsLimitedPartnerById(partner.id)}
                className={"underline text-[#bc8b20]"}
                rel="noopener noreferrer"
              >
                {partner.name}
              </a>
              {index < LP.length - 1 && ", "}
            </span>
          ) : (
            <span key={partner.id} className="cursor-default">
              {partner.name}
              {index < LP.length - 1 && ", "}
            </span>
          )
        )}
      </span>
    );
  };

  return (
    <div>
      <div className="lg:mt-4 px-4 w-[100%]">
        <div className="">
          <div className="lg:bg-white rounded-[6px]" x-show="tab === 1">
            <div className="w-full lg:grid lg:grid-cols-6  shadow-[0_2px_6px_0_rgba(0,0,0,0.08)]">
              <div className=" mt-3 lg:mt-0 rounded-lg lg:rounded lg:col-span-6 lg:border-r bg-white ">
                <div className="px-5 py-4 ">
                  <p className="flex text-[14pt] leading-[19pt] lg:text-[17px] text-[#333333] font-semibold lg:leading-[22px]">
                        Profile 
                        {profile?.linkedin ?  // Check if LinkedIn Profile value exists
                          <a href={`${profile["linkedin"]}`} target="_blank" className="mt-[0.20rem] ml-2"><img src={linkedin} width={16} alt="linkedin logo" /></a>
                          : ""
                        }
                    </p>
                  <div className="w-full bg-[#FFFFFF]">
                    {!isLoading ? (
                      <div className="grid grid-cols-2 md:grid-cols-4">
                          {investorProfileKeys?.map((keyObj, index) => {
                            const key = keyObj.accessor;
                            const label = keyObj.label;
                            let valueToRender = null;

                            if (key === "management_info") {
                              const managementInfo = profile[key];
                              if (managementInfo && managementInfo?.count > 0) {
                                valueToRender = (
                                  <>
                                    <div>
                                      {managementInfo?.data?.slice(0, 1)
                                        .map((info, i) => (
                                          <div key={i} style={{whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis"}} className="flex gap-1 flex-wrap items-center">
                                            {managementInfo?.count === 1 && info.email ? (
                                              <span className="text-[#0077b7] cursor-pointer" onClick={() => setShowContactPopup(true)}>{info.name}, {info.designation}</span>
                                            ) : (
                                              `${info.name}, ${info.designation}`
                                            )}
                                              {managementInfo?.count > 1 && ( <span className="text-[#0077b7] cursor-pointer"  onClick={() => setShowContactPopup(true)}>
                                                {` & ${managementInfo?.count - 1} more`}
                                              </span> )}
                                          </div>
                                        ))
                                      }
                                    </div>
                                    {showContactPopup && (
                                      <div
                                        style={{
                                          position: 'fixed',
                                          top: 0,
                                          left: 0,
                                          width: '100vw',
                                          height: '100vh',
                                          background: 'rgba(0,0,0,0.3)',
                                          zIndex: 9999,
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center'
                                        }}
                                        onClick={() => setShowContactPopup(false)}
                                      >
                                        <div
                                          style={{
                                            background: 'white',
                                            padding: 24,
                                            borderRadius: 8,
                                            width: 1300,
                                            minWidth: 1000,
                                            maxWidth: 2000,
                                            boxShadow: '0 2px 12px rgba(0,0,0,0.15)',
                                          }}
                                          onClick={e => e.stopPropagation()}
                                        >
                                          <div className="flex justify-end">
                                            <button
                                                onClick={() => setShowContactPopup(false)}
                                                className="rounded-full p-1 hover:bg-gray-100 transition-colors"
                                            >
                                                <X className="h-5 w-5 text-gray-500" />
                                            </button>
                                        </div>
                                          <div style={{overflowX: 'auto'}}>
                                            <table style={{width: '100%', borderCollapse: 'collapse', fontSize: 14}}>
                                              <thead>
                                                <tr style={{background: '#ffffffff'}}>
                                                  <th style={{padding: '6px 8px', border: '1px solid #ffffffff', textAlign: 'left'}}>Name</th>
                                                  <th style={{padding: '6px 8px', border: '1px solid #ffffffff', textAlign: 'left'}}>Designation</th>
                                                  <th style={{padding: '6px 8px', border: '1px solid #ffffffff', textAlign: 'left'}}>Email</th>
                                                  <th style={{padding: '6px 8px', border: '1px solid #ffffffff', textAlign: 'left'}}>Phone</th>
                                                  <th style={{padding: '6px 10px', border: '1px solid #ffffffff', textAlign: 'left'}}>Linked In</th>
                                                  <th style={{padding: '6px 8px', border: '1px solid #ffffffff', textAlign: 'left'}}>Sector Focus</th>
                                                </tr>
                                              </thead>
                                              <tbody>
                                                {managementInfo?.data?.map((info, idx) => (
                                                  <tr key={idx}>
                                                    <td style={{padding: '6px 8px', border: '1px solid #ffffffff'}}>{info.name || '-'}</td>
                                                    <td style={{padding: '6px 8px', border: '1px solid #ffffffff'}}>{info.designation || '-'}</td>
                                                    <td style={{padding: '6px 8px', border: '1px solid #ffffffff'}}>
                                                      {info.email ? (
                                                        <a href={`mailto:${info.email}`}>{info.email}</a>
                                                      ) : '-'}
                                                    </td>
                                                    <td style={{padding: '6px 8px', border: '1px solid #ffffffff'}}>
                                                      {info?.phone ? (
                                                        <span>{info.phone}</span>
                                                      ) : (
                                                        <div 
                                                          onClick={() => onGetPhoneNumber(info, idx)}
                                                          style={{
                                                            cursor: 'pointer',
                                                            color: loadingPhone[idx] ? '#9CA3AF' : '#10B981',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: '4px'
                                                          }}
                                                          title="Click to reveal phone number"
                                                        >
                                                          {loadingPhone[idx] ? (
                                                            <span style={{fontSize: '12px',}}>Loading...</span>
                                                          ) : (
                                                            <PhoneOutgoing size={16} style={{color: '#10B981'}} />
                                                          )}
                                                        </div>
                                                      )}
                                                    </td>
                                                    <td style={{padding: '6px 20px', border: '1px solid #ffffffff'}}>
                                                      {info.linkedin_url ? (
                                                        <a href={info.linkedin_url} target="_blank" rel="noopener noreferrer"><img src={linkedin} width={16} alt="linkedin logo" /></a>
                                                      ) : '-'}
                                                    </td>
                                                      <td style={{padding: '6px 8px', border: '1px solid #ffffffff'}}>{info.sector_focus ? <>{info.sector_focus} {renderSectorMatch(info.sector_match, info.sector_source_text, info.sector_source_url)}</> : '-'}</td>
                                                  </tr>
                                                ))}
                                              </tbody>
                                            </table>
                                          </div>
                                          <div className="flex justify-end">
                                            <button
                                              style={{
                                                marginTop: 12,
                                                background: '#a07924',
                                                color: 'white',
                                                border: 'none',
                                                borderRadius: 4,
                                                padding: '4px 16px',
                                                cursor: 'pointer',
                                                fontSize: 13
                                              }}
                                              onClick={() => setShowContactPopup(false)}
                                            >
                                              Close
                                            </button>
                                          </div>
                                        </div>
                                      </div>
                                    )}
                                  </>
                                );
                              } else valueToRender = null;
                            } else if (key === "dry_powder") {
                              profile?.assets_under_mgmt && (!profile?.hide_for_dry_powder) && getAlreadyInvestedAmount() ? valueToRender = (profile.assets_under_mgmt - getAlreadyInvestedAmount()).toFixed(2) : valueToRender = null;
                            } else if( key === "address"){
                              const add = profile && profile[key]?.trim();
                              const address2 = profile["address2"]?.trim();
                              if(add && address2){
                                // Check if address1 already ends with a comma
                                const separator = add.endsWith(',') ? ' ' : ', ';
                                valueToRender = `${add}${separator}${address2}`;
                              } else if (add){
                                valueToRender = add;
                              } else if (address2){
                                valueToRender = address2;
                              } else valueToRender = null;
                            } else if (key === "country") {
                              // Check if country headquarters is the same as country
                              const countryValue = profile["investor_country"]?.trim();
                              const countryHQValue = profile[key]?.trim();
                              
                              // Only render country headquarters if it's different from country and not "--"
                              if (countryHQValue && countryHQValue !== "" && countryHQValue !== "--" && countryHQValue !== countryValue) {
                                valueToRender = countryHQValue;
                              } else {
                                valueToRender = null;
                              }
                            } else if (key === "already_invested"){
                              valueToRender = profile?.assets_under_mgmt && getAlreadyInvestedAmount() != 0.00 ? getAlreadyInvestedAmount() : null
                            } else if (key === "exit_amount"){
                              valueToRender = profile?.assets_under_mgmt && parseFloat(profile?.exit_amount) > 0 && (!profile?.hide_exit_amount) ? parseFloat(profile?.exit_amount) : null
                            } else if(key === "limited_partners_fks"){
                              valueToRender = getLimitedPartners(profile[key])
                            } else {
                              valueToRender =
                                profile[key] && String(profile[key]).trim() !== "" && profile[key] !== "0.00" && profile[key] !== "--" && profile[key] != 0
                                  ? profile[key]
                                  : null;
                            }
                            const isMandatory = keyObj.mandatory === true || (keyObj.mandatory === "pevc" && !isAngelOnly);
                            if ((valueToRender == null || valueToRender === "") && isMandatory) valueToRender = "--";
                            if (valueToRender !== null) {
                              return (
                                <div key={index} className={`profiletable`}>
                                  <>
                                    <div className="leading-[21px] font-sans_book_body mt-1">
                                        <div class=" block lg:overflow-x-clip lg:overflow-ellipsis lg:whitespace-nowrap md:max-w-[14rem]">
                                            <span
                                            className="py-1 text-[10pt] lg:whitespace-nowrap leading-[18pt] lg:text-[13px] text-[#333333] cursor-auto"
                                            readOnly
                                            >
                                            {label}
                                            {key === "already_invested" && (
                                              <Tooltip 
                                                title={<span style={{ color: 'black' }}>As per available data - including split in case of&nbsp;co-investments</span>} 
                                                color="white" 
                                                placement="top"
                                              >
                                                <span 
                                                  style={{ 
                                                    marginLeft: 6, 
                                                    cursor: 'pointer', 
                                                    color: '#a07924', 
                                                    fontWeight: 'bold', 
                                                    borderRadius: '50%', 
                                                    background: '#f3e9d1', 
                                                    display: 'inline-flex', 
                                                    alignItems: 'center', 
                                                    justifyContent: 'center', 
                                                    width: 14, 
                                                    height: 14, 
                                                    fontSize: 10, 
                                                    border: '1px solid #e0c88f',
                                                    fontStyle: 'italic'
                                                  }}
                                                >
                                                  i
                                                </span>
                                              </Tooltip>
                                            )}
                                            </span>
                                        </div>
                                        {label === "Website" && valueToRender !== "--" ? (
                                            <a
                                            href={valueToRender.startsWith('http://') || valueToRender.startsWith('https://') ? valueToRender : `http://${valueToRender}`}
                                            className="mt-1 mb-2 text-[10pt] leading-[18pt] lg:text-[13px] text-[#333333] cursor-pointer font-semibold"
                                            target="_blank"
                                            >
                                            {valueToRender}
                                            </a>
                                        ) : (
                                            <p
                                            className="mt-1 mb-2 w-[90%] text-[10pt] leading-[18pt] lg:text-[13px] text-[#333333] cursor-auto font-semibold"
                                            readOnly
                                            >
                                            {valueToRender}
                                            </p>
                                        )}
                                    </div>
                                  </>
                                </div>
                              );
                            } else {
                              return null;
                            }
                          })}
                      </div>
                    ) : (
                      <div className="h-[10rem] w-full bg-[#FFFFFF] flex items-center justify-center">
                        <Loader />
                      </div>
                    )}
                  </div>
                </div>
              </div>
              {/* <!--first half end--> */}

              {/* <!--end--> */}
            </div>
          </div>
        </div>
      </div>
      {showConfirmPopup && confirmPopup()}
      {showPaymentPopup && paymentPopup()}
      {showEmailOptions && emailSelector()}
    </div>
  );
};

export default InvestorProfileHighlights;
